import { Stack } from '@mui/material';
import { useState } from 'react';
import { FilterCategory } from 'models/enums';
import Select, { Value } from 'domains/core/components/Select';
import { Send } from 'domains/campaigns/types';
import {
    ChannelSegmentFilter,
    RecordTypeFilter,
    SegmentFilter,
    SegmentFilterCategorySpecificField,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import ActivityFilter from './ActivityFilter';
import DemographicFilter from './DemographicFilter';
import EventFilter from './EventFilter';
import DeleteFilter from './DeleteFilter';
import IncludeNullValuesCheckbox from './IncludeNullValuesCheckbox';

const filterCategories: Value[] = Object.values(FilterCategory).map((category) => ({
    label: category,
    value: category,
}));

export type Props = {
    eventRecordTypeFilters: RecordTypeFilter[];
    demographicFilters: SegmentFilter[];
    engagementFilters: SegmentFilter[] | ChannelSegmentFilter[];
    filter: SegmentFilterState<SegmentFilterCategorySpecificField>;
    sends: Send[];
    handleDatetimeError: (filterId: string, error: boolean) => void;
    onUpdate: (filter: SegmentFilterState<SegmentFilterCategorySpecificField>, resetPreview?: boolean) => void;
    onRemove: () => void;
};

const Filter = ({
    eventRecordTypeFilters,
    demographicFilters,
    engagementFilters,
    filter,
    sends,
    handleDatetimeError,
    onUpdate,
    onRemove,
}: Props) => {
    const isCategoryActivity = Boolean(filter.category === FilterCategory.CONTACT_ACTIVITY);
    const isCategoryDetails = Boolean(filter.category === FilterCategory.CONTACT_DETAILS);
    const isCategoryEvent = Boolean(filter.category === FilterCategory.CONTACT_EVENTS);

    const [isFilterHighlighted, setIsFilterHighlighted] = useState(false);
    const [isOperatorValueHighlighted, setIsOperatorValueHighlighted] = useState(false);

    // Bridge functions for child components that still use the (index, filter, resetPreview?) signature.
    // The index parameter is ignored — tree path-based operations are handled upstream.
    const updateSegmentFilter = (
        _index: number,
        updatedFilter: SegmentFilterState<SegmentFilterCategorySpecificField>,
        resetPreview?: boolean
    ) => onUpdate(updatedFilter, resetPreview);

    const removeFilter = (_index: number) => onRemove();

    // Dummy index passed to child components that expect it in their props.
    // The actual value is unused because updateSegmentFilter/removeFilter ignore it.
    const index = 0;

    return (
        <Stack>
            <Stack direction="row" flexWrap="wrap">
                <Stack
                    columnGap={2}
                    direction="row"
                    flexWrap="wrap"
                    rowGap={2}
                    sx={{
                        backgroundColor: isFilterHighlighted ? 'action.hover' : 'transparent',
                        border: '1px solid',
                        borderColor: 'other.divider',
                        borderRadius: '6px',
                        padding: '32px 22px',
                    }}
                >
                    <Stack order="1" rowGap={1.5}>
                        <Select
                            data-test="category-select"
                            data-testid="category-select"
                            label="Filter type"
                            sx={{ textTransform: 'capitalize', width: '12rem' }}
                            value={filter.category ?? ''}
                            values={filterCategories}
                            onChange={(e: any) => {
                                onUpdate({
                                    ...filter,
                                    category: e.target.value,
                                    categorySpecificFields: {
                                        value: '',
                                    },
                                    name: '',
                                    operator: null,
                                    autocompleteValue: null,
                                    autocompleteInputValue: '',
                                });
                            }}
                        />
                        {isCategoryDetails && (
                            <IncludeNullValuesCheckbox
                                isNullValuesIncluded={filter.includeNullValues}
                                updateFilter={() => {
                                    onUpdate({
                                        ...filter,
                                        includeNullValues: !filter.includeNullValues,
                                    });
                                }}
                            />
                        )}
                    </Stack>
                    {isCategoryActivity && (
                        <ActivityFilter
                            filter={filter as SegmentFilterState<SegmentFilterEngagementFields>}
                            index={index}
                            engagementFilters={engagementFilters}
                            sends={sends}
                            updateSegmentFilter={updateSegmentFilter}
                        />
                    )}
                    {isCategoryDetails && (
                        <DemographicFilter
                            filter={filter}
                            filterOptions={demographicFilters}
                            index={index}
                            handleDatetimeError={handleDatetimeError}
                            updateSegmentFilter={updateSegmentFilter}
                        />
                    )}
                    {isCategoryEvent && (
                        <EventFilter
                            eventRecordTypeFilters={eventRecordTypeFilters}
                            filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                            index={index}
                            isOperatorValueHighlighted={isOperatorValueHighlighted}
                            handleDatetimeError={handleDatetimeError}
                            updateSegmentFilter={updateSegmentFilter}
                        />
                    )}
                    <DeleteFilter
                        filter={filter}
                        index={index}
                        isCategoryEvent={isCategoryEvent}
                        removeFilter={removeFilter}
                        setIsFilterHighlighted={setIsFilterHighlighted}
                        setIsOperatorValueHighlighted={setIsOperatorValueHighlighted}
                        updateSegmentFilter={updateSegmentFilter}
                    />
                </Stack>
            </Stack>
            {/* matchType toggle has been moved to FilterGroup */}
        </Stack>
    );
};

export default Filter;
